<template>
  <div>
    <AdButton
      v-if="!expansion"
      type="expansionset"
      @click="openDialog"
      noMarginRight
    />
    <v-tooltip v-if="storedSets > 0" bottom lazy>
      <template #activator="{ on }">
        <v-chip
          v-on="on"
          class="green lighten-5">
          {{ $t('nstored', { "n": storedSets }) }}
        </v-chip>
      </template>
      <span>{{ $t('details-in-usage') }}</span>
    </v-tooltip>
    <v-dialog
        v-model="modal.showDialog"
        max-width="1100"
        scrollable
        persistent
      >
        <v-card>
          <AdCardTitle
            prependicon="mdi-camera-plus-outline"
          >
            {{ $t('value-set') }}
            {{ $t('expansion-set') }}
            <v-spacer> </v-spacer>
            <v-chip
              class="mt-3 mr-2"
              :color="expandedConceptCountNonActive > 0 ? 'red lighten-4' : 'light-green lighten-3'"
              label
            >
              <span>{{ $t('expansion-set') }}</span>
              <span v-if="!initialCounting">: {{ $n(expandedConceptCount) }} {{ $t('codes') }}</span>
              <span v-if="expandedConceptCountNonActive > 0">,
                {{ $t('inactive-codes-in-intension', { nonactive: $n(expandedConceptCountNonActive) }) }}
              </span>
            </v-chip>
          </AdCardTitle>
          <v-card-text>
            <!-- show button to expand, along with the count info -->
            <AdInfoOwl v-if="initialCounting" class="mt-3">
              {{ $t('calculating')}}
            </AdInfoOwl>
            <AdInfoOwl v-else-if="expandedConceptCount > maxCodesInSet" class="mt-3">
              {{ $t('toomany-for-expansion-set', { count: $n(expandedConceptCount), max: maxCodesInSet }) }}
            </AdInfoOwl>
            <AdInfoOwl v-else-if="expandedConceptCount > 0 && !(isExpanded || modal.loadingExpansionItems)" class="mt-3 mb-0">
              {{ $t('click-for-expansion-set', { count: $n(expandedConceptCount) }) }}
            </AdInfoOwl>
            <AdInfoOwl v-else-if="expandedConceptCount < 1" chat warning class="mt-3 mb-0">
              {{ $t('no-codes-in-set') }}
            </AdInfoOwl>
            <AdInfoOwl v-if="expandedConceptCountNonActive > 0" chat warning class="mt-3 mb-0">
              {{ $t('inactive-codes-in-intension-hint', { nonactive: $n(expandedConceptCountNonActive) }) }}
            </AdInfoOwl>
            <div v-if="!initialCounting && (isExpanded || modal.loadingExpansionItems)" class="mt-4">
              <v-data-table
                :items="expansionItems"
                :footer-props="{ 'items-per-page-options': [5, 10] }"
                :hide-default-footer="expansionItems.length < 5"
                dense
              >
                <template v-slot:header>
                  <thead>
                    <tr>
                      <th width="1">{{ $t("level") }}/{{ $t("type") }}</th>
                      <th width="1">
                        <span v-if="hasIncludeOrExclude" class="text-no-wrap">{{ $t("code") }} / {{ $t("method") }}</span>
                        <template v-else>{{ $t("code") }}</template>
                      </th>
                      <th width="1" v-if="itemsInValueSetHasOrdinal">{{ $t("ordinal") }}</th>
                      <th>
                        <span v-if="hasIncludeOrExclude" class="text-no-wrap">{{ $t("display-name") }} / {{ $t("definition") }}</span>
                        <template v-else>{{ $t("display-name") }}</template>
                      </th>
                      <th class="text-no-wrap">
                        {{ $t("codesystem") }}
                      </th>
                    </tr>
                  </thead>
                </template>

                <template v-slot:body="{ items, originalItemsLength }">
                  <tbody>
                    <tr v-if="modal.loadingExpansionItems">
                      <td colspan="5">
                        <v-progress-linear indeterminate color="orange darken-2" />
                      </td>
                    </tr>
                    <template v-for="(item, i) in items">
                      <TerminologyValueSetConceptItem
                        :key="`expansion-item-${i}`"
                        :itemsInValueSetHasOrdinal="itemsInValueSetHasOrdinal"
                        :item="item"
                        :showEditable="false"
                        hideDesignations
                      />
                    </template>
                    <!-- No search results -->
                    <tr v-if="originalItemsLength > 0 && !items.length">
                      <td colspan="5" class="pt-4">
                        <AdInfoOwl chat>{{ $t("no-matches") }}</AdInfoOwl>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
          <v-card-actions class="dialogbottomstrip">
            <v-spacer></v-spacer>
            <AdButton
              v-if="!isExpanded"
              type="add"
              :text="$t('expand-expansion-set')"
              :disabled="modal.expandedConceptCount < 1 || isExpanded || modal.loadingExpansionItems"
              color="green"
              @click="getValueSetExpansion(true)"
            />
            <AdButton
              v-else-if="!expansion && !readonly"
              type="save"
              :disabled="modal.expansionSetSaved || modal.savingExpansionSet"
              @click="saveExpansionSet"
            />
            <AdButton type="cancel" @click="closeDialog"/>
          </v-card-actions>
        </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import TerminologyService from '@/services/TerminologyService'
import TerminologyValueSetConceptItem from '@/components/Terminology/TerminologyValueSetConceptItem'
import { cloneDeep } from 'lodash'

const initialModal = {
  count: null,
  deletingExpansionSet: false,
  nonactive: null,
  expandedSet: null,
  loadingExpansionItems: false,
  showDialog: false,
  savingExpansionSet: false,
  expansionSetSaved: false
}

export default {
  components: {
    TerminologyValueSetConceptItem
  },
  name: 'TerminologyExpansionSetDialog',
  props: {
    expansion: {
      type: Object,
      default: null
    },
    readonly: {
      type: Boolean
    },
    storedSets: {
      type: String,
      default: '0'
    }
  },
  data () {
    return {
      modal: cloneDeep(initialModal),
      initialCounting: false
    }
  },
  computed: {
    ...mapGetters('terminology', ['valueSetItem']),
    expandedConceptCount () {
      return +this.modal.count
    },
    expansionItems () {
      return this.modal.expandedSet?.[0]?.items || []
    },
    expandedConceptCountNonActive () {
      return this.modal.nonactive || 0
    },
    hasIncludeOrExclude () {
      return !!this.itemsInValueSetItem?.some(item => item.is === 'include' || item.is === 'exclude')
    },
    itemsInValueSetHasOrdinal () {
      return this.expansionItems.some((item) => item.ordinal)
    },
    isExpanded () {
      return this.modal.expandedSet?.[0].expanded === 'true'
    },
    maxCodesInSet () {
      return +this.modal.expandedSet?.[0]?.max || 100
    }
  },
  methods: {
    async openDialog () {
      if (this.expansion !== null) {
        const items = this.expansion.expansion?.[0]?.concept || []
        items.forEach(item => {
          item.is = 'concept'
        })
        this.modal.expandedSet = [{
          items,
          expanded: 'true'
        }]
        this.modal.count = this.expansion.expansion?.[0]?.total || null
        this.modal.showDialog = true
        return
      }

      this.initialCounting = true
      try {
        this.modal.showDialog = true
        await this.getValueSetExpansion()
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      }
      this.initialCounting = false
    },
    closeDialog () {
      this.modal = cloneDeep(initialModal)
      this.$emit('close')
    },
    async getValueSetExpansion (expand = false, store = false) {
      try {
        const { id, effectiveDate } = this.valueSetItem
        this.modal.loadingExpansionItems = true
        const response = await TerminologyService.getValueSetExpansion(id, effectiveDate, { expand, store })
        this.modal.expandedSet = response.data.expansion || null
        this.modal.count = response.data.count
        this.modal.nonactive = response.data.nonactive
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      } finally {
        this.modal.loadingExpansionItems = false
      }
    },
    async saveExpansionSet () {
      this.modal.savingExpansionSet = true
      try {
        await this.getValueSetExpansion(true, true)
        this.$notify(this.$t('submission-save-success-label'), 'success')
      } catch (error) {
        const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
        this.$oops(errorMessage)
      } finally {
        this.modal.savingExpansionSet = false
        this.modal.expansionSetSaved = true
        this.$store.commit('terminology/incrementValueSetExpansionAssociationCount')
      }
    }
  }
}
</script>
